<template>
  <div class="cap-panel" :class="{ 'cap-panel--al-corriente': detail?.statusColor === 'green' }">
    <div class="cap-header">
      <div class="cap-header__top">
        <div class="cap-nav-arrows">
          <button class="cap-nav-btn" :disabled="!hasPrev" @click="navigate(-1)"><span class="mdi mdi-chevron-up"></span></button>
          <button class="cap-nav-btn" :disabled="!hasNext" @click="navigate(1)"><span class="mdi mdi-chevron-down"></span></button>
        </div>
        <span class="cap-tenant-name">{{ detail?.tenantName }}</span>
        <button class="cap-close-btn" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
      </div>

      <template v-if="detail">
        <div class="cap-id-line">
          <span class="cap-id-tag">{{ detail.plotName }}</span>
          <span v-if="detail.plotAreaM2">{{ detail.plotAreaM2 }} m²</span>
        </div>

        <div class="cap-summary">
          <div class="cap-summary__item">
            <span class="cap-summary__label">Cargos</span>
            <span class="cap-summary__value">{{ formatCurrency(detail.totalCurrent) }}</span>
          </div>
          <div class="cap-summary__item">
            <span class="cap-summary__label">Abonado</span>
            <span class="cap-summary__value" :class="paidClass">{{ formatCurrency(detail.paidCurrent) }}</span>
          </div>
          <div class="cap-summary__item">
            <span class="cap-summary__label">Saldo</span>
            <span class="cap-summary__value" :class="`cap-color--${detail.statusColor}`">{{ formatCurrency(detail.pendingCurrent) }}</span>
          </div>
          <span class="cap-status-pill" :class="`cap-status-pill--${detail.statusColor}`">{{ statusPillLabel }}</span>
        </div>

        <div class="cap-context-line" :class="`cap-color--${detail.statusColor}`">{{ contextLine }}</div>
      </template>
    </div>

    <div class="cap-dots">
      <button v-for="(b, i) in visibleBlocks" :key="b.key" class="cap-dot" :class="{ 'cap-dot--active': activeBlockIndex === i, 'cap-dot--payment': b.key === 'payment' }" @click="scrollToBlock(i)"></button>
    </div>

    <div v-if="loading" class="cap-loading">Cargando...</div>

    <Transition name="cap-fade" mode="out-in">
      <div v-if="detail" :key="contractId" class="cap-content" ref="contentEl" @scroll="onScroll">
        <!-- Bloque 1: Cargos del periodo -->
        <section class="cap-block" :ref="(el) => setBlockRef(el, 0)">
          <div class="cap-block__header">
            <span class="cap-block__title">Cargos de {{ periodLabel }}</span>
            <span class="cap-block__title-value">{{ formatCurrency(detail.charges.total) }}</span>
          </div>

          <div v-if="detail.charges.rent" class="cap-charge-section">
            <span class="cap-charge-subtitle">Renta</span>
            <div class="cap-charge-row">
              <span class="mdi mdi-file-document-outline cap-charge-icon"></span>
              <span class="cap-charge-text">Renta base · {{ detail.charges.rent.plotName }}</span>
              <span class="cap-charge-amount">{{ formatCurrency(detail.charges.rent.amount) }}</span>
              <span class="cap-charge-pill" :class="pillClass(detail.charges.rent.status)">{{ detail.charges.rent.status }}</span>
            </div>
          </div>

          <div v-if="detail.charges.service" class="cap-charge-section">
            <span class="cap-charge-subtitle">Servicios comunes</span>
            <div class="cap-charge-row">
              <span class="mdi mdi-cog-outline cap-charge-icon"></span>
              <span class="cap-charge-text">Servicios comunes · {{ periodLabel }}</span>
              <span class="cap-charge-amount">{{ formatCurrency(detail.charges.service.amount) }}</span>
              <span class="cap-charge-pill" :class="pillClass(detail.charges.service.status)">{{ detail.charges.service.status }}</span>
            </div>
          </div>

          <div v-if="detail.charges.other.length" class="cap-charge-section">
            <span class="cap-charge-subtitle">Cargos adicionales</span>
            <div v-for="c in detail.charges.other" :key="c.id" class="cap-charge-row">
              <span class="mdi mdi-wrench-outline cap-charge-icon"></span>
              <span class="cap-charge-text">
                {{ c.concept }}
                <a v-if="c.ticketCode" href="#" class="cap-charge-ticket-link" @click.prevent="$emit('open-ticket-hint', c.ticketCode)">{{ c.ticketCode }}</a>
              </span>
              <span class="cap-charge-amount">{{ formatCurrency(c.amount) }}</span>
            </div>
          </div>

          <div class="cap-charge-total">
            <span>Total</span>
            <span>{{ formatCurrency(detail.charges.total) }}</span>
          </div>

          <div v-if="detail.nextCharge" class="cap-info cap-info--next">
            <div class="cap-info__head">
              <span class="mdi mdi-calendar-arrow-right cap-info__icon"></span>
              <span class="cap-info__title">Próximo cargo · {{ detail.nextCharge.label }}</span>
              <span class="cap-info__amount">{{ formatCurrency(detail.nextCharge.amount) }}</span>
            </div>
            <p class="cap-info__text">
              Vence el {{ formatDueDate(detail.nextCharge.dueDate) }}. Todavía no es un cargo por cobrar: no suma a los KPIs ni a la cartera
              hasta que llegue su mes. Se muestra aquí como aviso.
            </p>
          </div>

          <div v-if="detail.credit && (detail.credit.available > 0 || detail.credit.reserved.length)" class="cap-info cap-info--credit">
            <div class="cap-info__head">
              <span class="mdi mdi-wallet-outline cap-info__icon"></span>
              <span class="cap-info__title">Saldo a favor</span>
              <span class="cap-info__amount">{{ formatCurrency(detail.credit.available + detail.credit.reserved.reduce((s, r) => s + r.amount, 0)) }}</span>
            </div>
            <p v-if="detail.credit.available > 0" class="cap-info__text">
              {{ formatCurrency(detail.credit.available) }} sin asignar: se aplicará solo al cargo pendiente más antiguo en cuanto exista uno.
            </p>
            <p v-for="r in detail.credit.reserved" :key="r.id" class="cap-info__text">
              {{ formatCurrency(r.amount) }} reservados para {{ r.label }}: se aplicarán cuando ese cargo se genere, sin tocar saldos anteriores.
              Mientras tanto no aparecen como cobrado en los KPIs.
            </p>
          </div>
        </section>

        <!-- Bloque 2: Registrar pago -->
        <section class="cap-block" :ref="(el) => setBlockRef(el, 1)">
          <div class="cap-block__header">
            <span class="mdi mdi-plus cap-block__plus-icon"></span>
            <span class="cap-block__title">Registrar pago</span>
          </div>

          <div class="cap-payment-form" :class="{ 'cap-payment-form--flash': savedFlash }">
            <div class="cap-payment-form__row">
              <input v-model.number="paymentForm.amount" type="number" class="cap-input cap-input--amount" :placeholder="detail.statusColor === 'green' ? 'Registrar pago adelantado...' : '$0.00'" />
              <select v-model="paymentForm.concept" class="cap-input cap-input--concept">
                <option v-if="detail.charges.rent && detail.charges.rent.status !== 'Pagado'" value="RENT">Renta del mes</option>
                <option v-if="detail.overdueTotal > 0" value="OVERDUE">Saldo vencido — {{ formatCurrency(detail.overdueTotal) }}</option>
                <option v-if="detail.openCharges.length" value="SPECIFIC_CHARGE">Cargo específico</option>
                <option value="ON_ACCOUNT">Saldo a favor (pago extra)</option>
                <option v-if="detail.nextCharge" value="RENT_NEXT">Adelanto: {{ detail.nextCharge.label.toLowerCase() }}</option>
              </select>
            </div>

            <div class="cap-payment-form__row cap-payment-form__row--date">
              <label class="cap-date-label" for="cap-pay-date">Fecha del pago</label>
              <input id="cap-pay-date" v-model="paymentForm.date" type="date" class="cap-input cap-input--date" />
            </div>

            <select v-if="paymentForm.concept === 'SPECIFIC_CHARGE'" v-model="paymentForm.chargeId" class="cap-input">
              <option :value="null">Selecciona el cargo</option>
              <option v-for="c in detail.openCharges" :key="c.id" :value="c.id">{{ openChargeLabel(c) }}</option>
            </select>

            <p v-if="paymentForm.concept === 'OVERDUE'" class="cap-form-note">
              Se aplica solo a los cargos vencidos, del más antiguo al más reciente. Los cargos del mes no se tocan.
              Para pagar uno en particular, usa «Cargo específico».
            </p>
            <p v-else-if="paymentForm.concept === 'ON_ACCOUNT'" class="cap-form-note">
              Queda como saldo a favor y se aplica al cargo pendiente más antiguo. Si no hay ninguno, espera al siguiente cargo.
            </p>

            <p v-if="paymentForm.concept === 'RENT_NEXT'" class="cap-form-note">
              Se guarda como saldo a favor reservado para esa renta y se aplica solo cuando se genere, sin tocar los saldos vencidos anteriores.
              Mientras tanto no aparece como cobrado en los KPIs.
            </p>
            <p v-else-if="nothingPending" class="cap-form-note">
              No hay cargos pendientes: este pago quedará como saldo a favor y se aplicará al siguiente cargo que se genere.
            </p>

            <div class="cap-payment-form__row2">
              <input v-model="paymentForm.reference" class="cap-input" placeholder="Número de transferencia, cheque, etc." />
              <button class="cap-register-btn" :disabled="!paymentForm.amount || paymentForm.amount <= 0 || (paymentForm.concept === 'SPECIFIC_CHARGE' && !paymentForm.chargeId)" @click="submitPayment">Registrar</button>
            </div>
          </div>
        </section>

        <!-- Bloque 3: Historial de pagos -->
        <section class="cap-block" :ref="(el) => setBlockRef(el, 2)">
          <div class="cap-block__header">
            <span class="cap-block__title">Pagos recibidos</span>
            <span class="cap-block__title-value" :class="detail.totalPaidThisPeriod > 0 ? 'cap-color--green' : ''">{{ formatCurrency(detail.totalPaidThisPeriod) }}</span>
          </div>

          <TransitionGroup name="cap-payment-row" tag="div">
            <div v-for="p in (showAllPayments ? detail.allPayments : detail.payments)" :key="p.id" class="cap-payment-row">
              <span class="mdi mdi-check-circle cap-payment-check"></span>
              <span class="cap-payment-date">{{ formatDateLong(p.date) }}</span>
              <span class="cap-payment-concept">{{ conceptLabel(p.concept) }}</span>
              <span class="cap-payment-amount">{{ formatCurrency(p.amount) }}</span>
              <span v-if="p.unapplied > 0" class="cap-payment-credit">{{ p.reservedLabel ? `Reservado: ${p.reservedLabel}` : 'Saldo a favor' }}</span>
              <button v-if="confirmPaymentId !== p.id" type="button" class="cap-payment-del" title="Eliminar pago" aria-label="Eliminar pago" @click="askDeletePayment(p.id)">
                <span class="mdi mdi-trash-can-outline"></span>
              </button>
              <div v-else class="cap-payment-confirm">
                <p class="cap-payment-confirm__text">
                  ¿Eliminar este pago de {{ formatCurrency(p.amount) }}? Se revierte lo que aplicó a los cargos y los saldos vuelven a su valor anterior.
                  <template v-if="p.legacy"> Es un pago anterior a esta versión: se revierte sobre el cargo principal al que se aplicó; revisa los saldos después.</template>
                </p>
                <p v-if="paymentDeleteError" class="cap-payment-confirm__error">{{ paymentDeleteError }}</p>
                <div class="cap-payment-confirm__actions">
                  <button type="button" class="cap-payment-confirm__cancel" :disabled="deletingPayment" @click="cancelDeletePayment">Cancelar</button>
                  <button type="button" class="cap-payment-confirm__ok" :disabled="deletingPayment" @click="confirmDeletePayment">{{ deletingPayment ? 'Eliminando...' : 'Sí, eliminar' }}</button>
                </div>
              </div>
            </div>
          </TransitionGroup>

          <div v-if="!detail.payments.length && !showAllPayments" class="cap-empty">
            <span class="mdi mdi-clock-outline cap-empty-icon"></span>
            <span class="cap-empty-text">Sin pagos registrados este periodo</span>
          </div>

          <button v-if="!showAllPayments && detail.allPayments.length > detail.payments.length" class="cap-link-btn" @click="showAllPayments = true">Ver historial completo</button>
        </section>

        <!-- Bloque 4: Saldos anteriores -->
        <section v-if="detail.priorPeriods.length" class="cap-block" :ref="(el) => setBlockRef(el, 3)">
          <div class="cap-block__header">
            <span class="cap-block__dot-red"></span>
            <span class="cap-block__title">Saldos anteriores</span>
            <span class="cap-block__title-value cap-color--red">{{ formatCurrency(detail.priorTotal) }}</span>
          </div>
          <div v-for="(p, i) in detail.priorPeriods" :key="i" class="cap-prior-row">
            <span class="cap-prior-month">{{ monthLabel(p.month, p.year) }}</span>
            <span class="cap-prior-original" :class="{ 'cap-prior-original--strike': p.paid > 0 }">{{ formatCurrency(p.original) }}</span>
            <span v-if="p.paid > 0" class="cap-prior-paid">{{ formatCurrency(p.paid) }}</span>
            <span class="cap-prior-pending">{{ formatCurrency(p.pending) }}</span>
          </div>
        </section>

        <!-- Bloque 5: Recordatorios -->
        <section v-if="detail.statusColor !== 'green'" class="cap-block" :ref="(el) => setBlockRef(el, 4)">
          <div class="cap-block__header">
            <span class="cap-block__title">Recordatorios enviados</span>
            <span class="cap-block__title-sub">({{ detail.reminders.length }})</span>
          </div>
          <div v-for="r in detail.reminders" :key="r.id" class="cap-reminder-row-wrap">
            <div class="cap-reminder-row">
              <span class="mdi mdi-email-outline cap-reminder-icon"></span>
              <div class="cap-reminder-info">
                <div class="cap-reminder-type">{{ reminderTypeLabel(r.type) }}</div>
                <div class="cap-reminder-meta">{{ r.recipient || 'Sin destinatario' }} · {{ formatDateLong(r.createdAt) }}</div>
              </div>
              <div class="cap-reminder-right">
                <span class="cap-reminder-time">Hace {{ r.daysAgo }}d</span>
                <span v-if="r.daysAgo > 7 && !r.responseText" class="cap-reminder-noresponse">Sin respuesta</span>
              </div>
            </div>

            <div v-if="r.responseText" class="cap-reminder-response" :class="r.commitmentDate ? 'cap-reminder-response--commitment' : 'cap-reminder-response--replied'">
              {{ r.commitmentDate ? `Compromiso: pagar el ${formatDateLong(r.commitmentDate)}` : 'Con respuesta' }}
            </div>
            <button v-else-if="respondingId !== r.id" class="cap-link-btn" @click="respondingId = r.id">Registrar respuesta</button>

            <div v-if="respondingId === r.id" class="cap-response-form">
              <textarea v-model="responseText" class="cap-note-input" rows="2" placeholder="¿Qué respondió el inquilino?"></textarea>
              <input v-model="commitmentDate" type="date" class="cap-input" />
              <button class="cap-register-btn" @click="submitResponse(r.id)">Guardar</button>
            </div>
          </div>
        </section>

        <!-- Envíos por correo y su estado de entrega -->
        <ContractMessages :contract-id="contractId" :version="loadCount" />

        <!-- Bloque 6: Notas de gestión -->
        <section class="cap-block" :ref="(el) => setBlockRef(el, detail.statusColor === 'green' ? 3 : 5)">
          <div class="cap-block__header">
            <span class="cap-block__title">Notas de gestión</span>
            <span class="mdi mdi-lock-outline cap-lock-icon"></span>
          </div>
          <div v-for="n in detail.notes" :key="n.id" class="cap-note-bubble">
            <div class="cap-note-text">{{ n.text }}</div>
            <div class="cap-note-meta">{{ n.author }} · {{ formatDateLong(n.createdAt) }}</div>
          </div>
          <textarea v-model="noteText" class="cap-note-input" placeholder="Agrega una nota sobre esta cuenta..." rows="2"></textarea>
          <button v-if="noteText.trim()" class="cap-note-save" @click="submitNote">Guardar</button>
        </section>
      </div>
    </Transition>

    <div v-if="detail" class="cap-actions">
      <button class="cap-action-primary" @click="scrollToBlock(1)">Registrar pago</button>
      <button class="cap-action-secondary" @click="showReminderForm = true">Enviar recordatorio</button>
      <div class="cap-more-wrap">
        <button class="cap-more-btn" @click="moreMenuOpen = !moreMenuOpen"><span class="mdi mdi-dots-horizontal"></span></button>
        <div v-if="moreMenuOpen" class="cap-more-menu"> 
          <button @click="showStatementPanel = true; moreMenuOpen = false">Generar estado de cuenta en PDF</button>          <button @click="onStub('Agregar cargo manual')">Agregar cargo manual</button>
          <button @click="scrollToBlock(detail.statusColor === 'green' ? 3 : 5); moreMenuOpen = false">Agregar nota de gestión</button>
          <button @click="onStub('Ver historial completo')">Ver historial completo</button>
          <button @click="onStub('Escalar a dirección')">Escalar a dirección</button>
        </div>
      </div>
    </div>

    <ReminderComposerPanel
        v-if="showReminderForm"
        :contract-id="contractId"
        :park-id="parkId"
        :pending-amount="detail.pendingCurrent"
        :due-date="detail.mainDueDate"
        :overdue-days="detail.overdueDays"
        :default-recipient="detail.paymentContact?.email || ''"
        @close="showReminderForm = false"
        @sent="onReminderSent"
        />

        <StatementGeneratorPanel
        v-if="showStatementPanel"
        :park-id="parkId"
        :contract-id="contractId"
        :period="period"
        :default-recipient="detail.paymentContact?.email || ''"
        @close="showStatementPanel = false"
        @generated="showStatementPanel = false"
        />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { getAccountDetail, registerPayment, deletePayment, addManagementNote, registerReminderResponse } from '@/services/charges'
import ReminderComposerPanel from './ReminderComposerPanel.vue'
import StatementGeneratorPanel from './StatementGeneratorPanel.vue'
import ContractMessages from './ContractMessages.vue'

const showStatementPanel = ref(false)

const props = defineProps({
  contractId: { type: String, required: true },
  parkId: { type: String, required: true },
  period: { type: String, required: true },
  accountList: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'navigate', 'refresh', 'open-ticket-hint'])

const detail = ref(null)
const loading = ref(true)
const loadCount = ref(0) // sube en cada recarga para refrescar los envíos
const showAllPayments = ref(false)
const noteText = ref('')
const savedFlash = ref(false)
const moreMenuOpen = ref(false)
const showReminderForm = ref(false)

const respondingId = ref(null)
const responseText = ref('')
const commitmentDate = ref('')

const paymentForm = ref({ amount: null, concept: 'SPECIFIC_CHARGE', chargeId: null, date: new Date().toISOString().slice(0, 10), reference: '' })

async function load() {
  loading.value = true
  showAllPayments.value = false
  try {
    detail.value = await getAccountDetail(props.contractId, props.period)
    paymentForm.value.concept = defaultConcept()
    paymentForm.value.chargeId = null
    loadCount.value++
  } finally {
    loading.value = false
  }
}
onMounted(load)
watch(() => props.contractId, load)

const currentIndex = computed(() => props.accountList.findIndex((r) => r.contractId === props.contractId))
const hasPrev = computed(() => currentIndex.value > 0)
const hasNext = computed(() => currentIndex.value >= 0 && currentIndex.value < props.accountList.length - 1)
function navigate(delta) {
  const next = props.accountList[currentIndex.value + delta]
  if (next) emit('navigate', next.contractId)
}

const periodLabel = computed(() => {
  const [y, m] = props.period.split('-').map(Number)
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  return `${MONTHS[m - 1]} ${y}`
})
function monthLabel(m, y) {
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  return `${MONTHS[m - 1]} ${y}`
}

const statusPillLabel = computed(() => {
  if (!detail.value) return ''
  if (detail.value.statusColor === 'green') return 'Al corriente'
  if (detail.value.overdueDays > 0) return `Vencido ${detail.value.overdueDays}d`
  return 'Pendiente'
})

const contextLine = computed(() => {
  const d = detail.value
  if (!d) return ''
  if (d.statusColor === 'green') return 'Al corriente · Sin saldo pendiente'
  if (d.overdueDays > 0) return `Vencido hace ${d.overdueDays} días${d.lastReminderDaysAgo !== null ? ` · Último recordatorio hace ${d.lastReminderDaysAgo} días` : ''}`
  return `Pendiente de pago · Vence el ${formatDueDate(d.mainDueDate)}`
})

const paidClass = computed(() => {
  const d = detail.value
  if (!d) return ''
  if (d.paidCurrent >= d.totalCurrent && d.totalCurrent > 0) return 'cap-color--green'
  if (d.paidCurrent === 0) return 'cap-color--gray'
  return 'cap-color--amber'
})

const visibleBlocks = computed(() => {
  if (!detail.value) return []
  const blocks = [{ key: 'charges' }, { key: 'payment' }, { key: 'history' }]
  if (detail.value.priorPeriods.length) blocks.push({ key: 'prior' })
  if (detail.value.statusColor !== 'green') blocks.push({ key: 'reminders' })
  blocks.push({ key: 'notes' })
  return blocks
})

function pillClass(status) {
  if (status === 'Pagado') return 'cap-charge-pill--green'
  if (status === 'Parcial') return 'cap-charge-pill--amber'
  return `cap-charge-pill--${detail.value?.statusColor || 'blue'}`
}

function conceptLabel(c) {
  return { FULL: 'Pago total', RENT: 'Renta', SERVICE: 'Servicios', SPECIFIC_CHARGE: 'Cargo específico', ON_ACCOUNT: 'Saldo a favor', OVERDUE: 'Saldo vencido' }[c] || c
}

const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
// «Concepto · mes — pendiente $X», marcando los vencidos.
function openChargeLabel(c) {
  const when = c.periodYear && c.periodMonth ? ` · ${MONTHS_SHORT[c.periodMonth - 1]} ${c.periodYear}` : ''
  return `${c.concept}${when} — pendiente ${formatCurrency(c.remaining)}${c.overdue ? ' (vencido)' : ''}`
}
// Concepto sugerido al abrir: lo vencido primero; si no, la renta del mes; si no, un cargo específico.
function defaultConcept() {
  const d = detail.value
  if (d?.overdueTotal > 0) return 'OVERDUE'
  if (d?.charges?.rent && d.charges.rent.status !== 'Pagado') return 'RENT'
  return d?.openCharges?.length ? 'SPECIFIC_CHARGE' : 'ON_ACCOUNT'
}
function reminderTypeLabel(t) {
  return { PREVENTIVE: 'Recordatorio preventivo', COLLECTION: 'Recordatorio de cobro', FORMAL: 'Notificación formal' }[t] || t
}

const nothingPending = computed(() => {
  const d = detail.value
  if (!d || !['ON_ACCOUNT', 'RENT', 'OVERDUE'].includes(paymentForm.value.concept)) return false
  return d.pendingCurrent <= 0 && d.priorTotal <= 0
})

async function submitPayment() {
  if (!paymentForm.value.amount || paymentForm.value.amount <= 0) return
  let [year, month] = props.period.split('-').map(Number)
  let concept = paymentForm.value.concept
  if (concept === 'RENT_NEXT' && detail.value?.nextCharge) {
    concept = 'RENT'
    year = detail.value.nextCharge.periodYear
    month = detail.value.nextCharge.periodMonth
  }
  await registerPayment(props.contractId, { ...paymentForm.value, concept, periodYear: year, periodMonth: month })
  paymentForm.value = { amount: null, concept: defaultConcept(), chargeId: null, date: new Date().toISOString().slice(0, 10), reference: '' }
  savedFlash.value = true
  setTimeout(() => (savedFlash.value = false), 500)
  await load()
  emit('refresh')
}

const confirmPaymentId = ref(null)
const deletingPayment = ref(false)
const paymentDeleteError = ref('')

function askDeletePayment(id) {
  confirmPaymentId.value = id
  paymentDeleteError.value = ''
}

function cancelDeletePayment() {
  confirmPaymentId.value = null
  paymentDeleteError.value = ''
}

async function confirmDeletePayment() {
  deletingPayment.value = true
  paymentDeleteError.value = ''
  try {
    await deletePayment(props.contractId, confirmPaymentId.value)
    confirmPaymentId.value = null
    await load()
    emit('refresh')
  } catch (err) {
    paymentDeleteError.value = err.message
  } finally {
    deletingPayment.value = false
  }
}

async function onReminderSent() {
  showReminderForm.value = false
  await load()
}

async function submitResponse(reminderId) {
  await registerReminderResponse(reminderId, responseText.value, commitmentDate.value || null)
  respondingId.value = null
  responseText.value = ''
  commitmentDate.value = ''
  await load()
}

async function submitNote() {
  if (!noteText.value.trim()) return
  await addManagementNote(props.contractId, noteText.value)
  noteText.value = ''
  await load()
}

function onStub(label) {
  moreMenuOpen.value = false
  console.log(`TODO: ${label} — pendiente de construir`)
}

const contentEl = ref(null)
const blockRefs = ref([])
const activeBlockIndex = ref(0)
function setBlockRef(el, i) { if (el) blockRefs.value[i] = el }
function scrollToBlock(i) { blockRefs.value[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
function onScroll() {
  if (!contentEl.value) return
  const el = contentEl.value
  const scrollTop = el.scrollTop
  const maxScrollTop = el.scrollHeight - el.clientHeight
  let closest = 0, closestDist = Infinity
  blockRefs.value.forEach((elRef, i) => {
    if (!elRef) return
    const effectiveOffset = Math.min(elRef.offsetTop, maxScrollTop)
    const dist = Math.abs(effectiveOffset - scrollTop)
    if (dist < closestDist) { closestDist = dist; closest = i }
  })
  activeBlockIndex.value = closest
}

function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0) }
// Los vencimientos son fechas de calendario (sin hora): se leen en UTC para que no se recorran un día en horario de México.
function formatDueDate(iso) { return iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—' }
function formatDateLong(iso) { return iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' }
</script>

<style scoped>
.cap-panel { flex: 0 0 40%; max-width: 40%; display: flex; flex-direction: column; background: #ffffff; border: 1px solid var(--border-active); border-radius: 10px; overflow: hidden; margin: 0.75rem 1.5rem 1.5rem 0; position: relative; min-height: 0; }

.cap-header { flex-shrink: 0; padding: 0.75rem 1.25rem calc(0.75rem) calc(1.25rem + 12px); border-bottom: 2px solid #f1f5f9; }
.cap-panel--al-corriente .cap-header { border-bottom-color: #10b981; }

.cap-header__top { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.3rem; }
.cap-nav-arrows { display: flex; flex-direction: column; }
.cap-nav-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; padding: 0; line-height: 0.7; }
.cap-nav-btn:hover:not(:disabled) { color: #1e3a5f; }
.cap-nav-btn:disabled { opacity: 0.3; }
.cap-tenant-name { flex: 1; font-family: var(--font-display); font-weight: 700; font-size: 18px; color: #1e3a5f; }
.cap-close-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.cap-id-line { display: flex; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #64748b; margin-bottom: 0.5rem; }
.cap-id-tag { background: #f1f5f9; border-radius: 4px; padding: 0.05rem 0.35rem; }

.cap-summary { display: flex; align-items: center; gap: 0.5rem; }
.cap-summary__item { flex: 1; display: flex; flex-direction: column; border-right: 1px solid #f1f5f9; padding-right: 0.5rem; }
.cap-summary__item:last-of-type { border-right: none; }
.cap-summary__label { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.cap-summary__value { font-family: var(--font-mono); font-weight: 700; font-size: 16px; color: #1e3a5f; }

.cap-color--green { color: #10b981; } .cap-color--amber { color: #f0a500; } .cap-color--orange { color: #f97316; } .cap-color--red { color: #e84545; } .cap-color--blue { color: #3b82f6; } .cap-color--gray { color: #94a3b8; }

.cap-status-pill { height: 24px; display: flex; align-items: center; padding: 0 0.6rem; border-radius: 20px; font-family: var(--font-body); font-size: 12px; font-weight: 500; flex-shrink: 0; }
.cap-status-pill--green { background: #dcfce7; color: #16a34a; }
.cap-status-pill--blue { background: #dbeafe; color: #2563eb; }
.cap-status-pill--amber { background: #fef3c7; color: #d97706; }
.cap-status-pill--orange { background: #ffedd5; color: #ea580c; }
.cap-status-pill--red { background: #fee2e2; color: #dc2626; }

.cap-context-line { font-family: var(--font-body); font-size: 10px; margin-top: 0.3rem; }

.cap-dots { position: absolute; left: 4px; top: 130px; bottom: 60px; width: 4px; display: flex; flex-direction: column; justify-content: center; gap: 10px; z-index: 10; }
.cap-dot { width: 6px; height: 6px; border-radius: 50%; background: #e2e8f0; border: none; cursor: pointer; padding: 0; }
.cap-dot--active { background: #1e3a5f; }
.cap-dot--payment { background: #3b82f6 !important; }

.cap-loading { padding: 2rem; text-align: center; color: var(--text-secondary); font-family: var(--font-body); font-size: 13px; }

.cap-content { flex: 1; min-height: 0; overflow-y: auto; padding-left: 12px; }
.cap-content::-webkit-scrollbar { width: 6px; }
.cap-content::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.cap-content::-webkit-scrollbar-track { background: transparent; }

.cap-fade-enter-active, .cap-fade-leave-active { transition: opacity 150ms ease; }
.cap-fade-enter-from, .cap-fade-leave-to { opacity: 0; }

.cap-block { padding: 1rem 1.25rem; border-bottom: 1px solid #f1f5f9; }
.cap-block__header { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.6rem; }
.cap-block__title { font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; }
.cap-block__title-sub { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.cap-block__title-value { margin-left: auto; font-family: var(--font-mono); font-size: 11px; color: #1e3a5f; }
.cap-block__plus-icon { color: #3b82f6; font-size: 12px; }
.cap-block__dot-red { width: 6px; height: 6px; border-radius: 50%; background: #e84545; }

.cap-charge-section { margin-bottom: 0.6rem; }
.cap-charge-subtitle { display: block; font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-bottom: 0.2rem; }
.cap-charge-row { display: flex; align-items: center; gap: 0.5rem; height: 32px; font-family: var(--font-body); font-size: 13px; }
.cap-charge-icon { color: #94a3b8; font-size: 14px; flex-shrink: 0; }
.cap-charge-text { flex: 1; color: #1e3a5f; min-width: 0; }
.cap-charge-ticket-link { color: #3b82f6; margin-left: 0.3rem; font-size: 11px; }
.cap-charge-amount { font-family: var(--font-mono); color: #1e3a5f; }
.cap-charge-pill { font-size: 10px; padding: 0.05rem 0.35rem; border-radius: 10px; margin-left: 0.4rem; }
.cap-charge-pill--green { background: #dcfce7; color: #16a34a; }
.cap-charge-pill--amber { background: #fef3c7; color: #d97706; }
.cap-charge-pill--blue { background: #dbeafe; color: #2563eb; }
.cap-charge-pill--orange { background: #ffedd5; color: #ea580c; }
.cap-charge-pill--red { background: #fee2e2; color: #dc2626; }

.cap-charge-total { display: flex; justify-content: space-between; background: #f8f9fc; padding: 0.4rem 0.5rem; border-radius: 4px; font-family: var(--font-mono); font-weight: 700; font-size: 13px; color: #1e3a5f; margin-top: 0.4rem; }

.cap-payment-form { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 0.6rem; display: flex; flex-direction: column; gap: 0.5rem; transition: background 150ms ease; }
.cap-panel--al-corriente .cap-payment-form { background: #f0fdf4; border-color: #bbf7d0; }
.cap-payment-form--flash { background: #dcfce7 !important; }

.cap-payment-form__row { display: flex; gap: 0.4rem; }
.cap-payment-form__row2 { display: flex; gap: 0.4rem; align-items: center; }
.cap-input { font-family: var(--font-body); font-size: 12px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.4rem 0.5rem; background: #ffffff; box-sizing: border-box; }
.cap-input--amount { flex: 1 1 0; min-width: 0; font-family: var(--font-mono); font-size: 14px; }
.cap-input--concept { flex: 1 1 0; min-width: 0; }
.cap-payment-form__row--date { align-items: center; }
.cap-date-label { font-family: var(--font-body); font-size: 11px; color: #64748b; flex-shrink: 0; }
.cap-input--date { flex: 1 1 0; min-width: 0; }
.cap-payment-form { min-width: 0; overflow-x: hidden; }
.cap-payment-form__row2 .cap-input { flex: 1; }

.cap-register-btn { background: #3b82f6; color: #ffffff; border: none; border-radius: 4px; padding: 0.4rem 0.75rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; cursor: pointer; flex-shrink: 0; }
.cap-register-btn:disabled { opacity: 0.4; cursor: default; }

.cap-payment-row { display: flex; align-items: center; gap: 0.5rem; min-height: 40px; flex-wrap: wrap; font-family: var(--font-body); font-size: 12px; }
.cap-payment-check { color: #10b981; font-size: 14px; }
.cap-payment-date { color: #64748b; flex: 0 0 80px; }
.cap-payment-concept { color: #1e3a5f; flex: 1; }
.cap-payment-amount { font-family: var(--font-mono); color: #10b981; font-weight: 600; }
.cap-payment-del { background: none; border: none; color: #cbd5e1; cursor: pointer; display: flex; padding: 0.1rem; font-size: 15px; }
.cap-payment-del:hover { color: #dc2626; }
.cap-payment-confirm { flex: 1 0 100%; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 0.5rem 0.65rem; margin-bottom: 0.4rem; }
.cap-payment-confirm__text { margin: 0; font-size: 11px; line-height: 1.45; color: #7f1d1d; }
.cap-payment-confirm__error { margin: 0.35rem 0 0; font-size: 11px; color: #dc2626; }
.cap-payment-confirm__actions { display: flex; gap: 0.4rem; margin-top: 0.5rem; }
.cap-payment-confirm__cancel { height: 28px; padding: 0 0.7rem; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; font-size: 11px; cursor: pointer; }
.cap-payment-confirm__ok { height: 28px; padding: 0 0.7rem; border-radius: 6px; border: none; background: #dc2626; color: #fff; font-size: 11px; cursor: pointer; }
.cap-payment-confirm__cancel:disabled, .cap-payment-confirm__ok:disabled { opacity: 0.5; cursor: default; }

.cap-form-note { margin: 0; font-family: var(--font-body); font-size: 11px; line-height: 1.45; color: #1d4ed8; }
.cap-payment-credit { font-size: 10px; color: #7c3aed; background: #ede9fe; border-radius: 4px; padding: 0.05rem 0.35rem; white-space: nowrap; }
.cap-info { margin-top: 0.7rem; border-radius: 8px; padding: 0.55rem 0.65rem; font-family: var(--font-body); }
.cap-info--next { background: #f8f9fc; border: 1px dashed #cbd5e1; }
.cap-info--credit { background: #f5f3ff; border: 1px solid #ddd6fe; }
.cap-info__head { display: flex; align-items: center; gap: 0.4rem; }
.cap-info__icon { font-size: 16px; color: #64748b; }
.cap-info--credit .cap-info__icon { color: #7c3aed; }
.cap-info__title { flex: 1; min-width: 0; font-size: 12px; font-weight: 500; color: #1e3a5f; }
.cap-info__amount { font-family: var(--font-mono); font-size: 12px; color: #1e3a5f; }
.cap-info__text { margin: 0.35rem 0 0; font-size: 11px; line-height: 1.45; color: #64748b; }
.cap-payment-row-enter-active { transition: all 200ms ease; }
.cap-payment-row-enter-from { opacity: 0; transform: translateY(-8px); }

.cap-empty { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; padding: 1.5rem; }
.cap-empty-icon { font-size: 24px; color: #cbd5e1; }
.cap-empty-text { font-family: var(--font-body); font-size: 12px; color: #94a3b8; }

.cap-link-btn { background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 12px; cursor: pointer; margin-top: 0.3rem; padding: 0; display: block; }

.cap-prior-row { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; height: 36px; align-items: center; font-family: var(--font-mono); font-size: 12px; }
.cap-prior-month { font-family: var(--font-body); font-weight: 500; color: #1e3a5f; }
.cap-prior-original { color: #94a3b8; }
.cap-prior-original--strike { text-decoration: line-through; }
.cap-prior-paid { color: #10b981; }
.cap-prior-pending { color: #dc2626; font-weight: 600; }

.cap-reminder-row-wrap { padding: 0.4rem 0; border-bottom: 1px solid #f8fafc; }
.cap-reminder-row { display: flex; align-items: center; gap: 0.5rem; min-height: 40px; }
.cap-reminder-icon { color: #64748b; font-size: 14px; }
.cap-reminder-info { flex: 1; min-width: 0; }
.cap-reminder-type { font-family: var(--font-body); font-size: 12px; font-weight: 500; color: #1e3a5f; }
.cap-reminder-meta { font-family: var(--font-body); font-size: 11px; color: #64748b; }
.cap-reminder-right { display: flex; flex-direction: column; align-items: flex-end; gap: 0.1rem; }
.cap-reminder-time { font-family: var(--font-body); font-size: 10px; color: #cbd5e1; }
.cap-reminder-noresponse { font-family: var(--font-body); font-size: 9px; color: #d97706; }

.cap-reminder-response { font-family: var(--font-body); font-size: 11px; padding: 0.2rem 0.5rem; border-radius: 4px; display: inline-block; margin-top: 0.2rem; }
.cap-reminder-response--replied { background: #dcfce7; color: #16a34a; }
.cap-reminder-response--commitment { background: #dbeafe; color: #2563eb; }

.cap-response-form { display: flex; flex-direction: column; gap: 0.4rem; margin-top: 0.4rem; padding: 0.5rem; background: #f8f9fc; border-radius: 6px; }

.cap-lock-icon { margin-left: auto; color: #94a3b8; font-size: 12px; }
.cap-note-bubble { background: #f8f9fc; border-radius: 8px; padding: 10px; margin-bottom: 0.5rem; }
.cap-note-text { font-family: var(--font-body); font-size: 13px; color: #1e3a5f; }
.cap-note-meta { font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-top: 0.3rem; }
.cap-note-input { width: 100%; font-family: var(--font-body); font-size: 13px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.5rem; resize: vertical; box-sizing: border-box; }
.cap-note-save { margin-top: 0.4rem; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.4rem 0.75rem; font-family: var(--font-body); font-size: 12px; cursor: pointer; float: right; }

.cap-actions { flex-shrink: 0; height: 56px; display: flex; align-items: center; gap: 0.5rem; padding: 0 1rem; border-top: 1px solid #e2e8f0; }
.cap-action-primary { flex: 0 0 45%; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; height: 36px; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.cap-action-secondary { flex: 0 0 40%; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; height: 36px; color: #1e3a5f; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.cap-more-wrap { position: relative; flex: 1; display: flex; justify-content: flex-end; }
.cap-more-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.cap-more-menu { position: absolute; bottom: 100%; right: 0; margin-bottom: 4px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15,23,42,0.12); min-width: 220px; padding: 0.3rem; z-index: 50; }
.cap-more-menu button { display: block; width: 100%; text-align: left; background: none; border: none; font-family: var(--font-body); font-size: 12px; color: var(--text-content); padding: 0.4rem 0.5rem; cursor: pointer; border-radius: 5px; }
.cap-more-menu button:hover { background: #f8fafc; }
</style>